import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarBlank, MapPin, ArrowUpRight, ArrowRight, Repeat, Sparkle } from "@phosphor-icons/react";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";
import { useLanguage } from "@/i18n/LanguageContext";
import { guideForEvent } from "@/data/eventGuides";
import { countdownLabel, countdownTier } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exported so the event guide pages format dates identically.
export const INTL_LOCALE = {
  en: "en-US", es: "es", pt: "pt-BR", fr: "fr", de: "de", nl: "nl", he: "he", zh: "zh-CN",
};

const CATEGORY_COLORS = {
  "LGBTQ+ & Pride": "bg-sunset/12 text-sunset",
  Boating: "bg-emerald/8 text-emerald",
  Holiday: "bg-sunset/12 text-sunset",
  Arts: "bg-emerald/8 text-emerald",
  Festival: "bg-sunset/12 text-sunset",
  Music: "bg-emerald/8 text-emerald",
  "Air & Sea": "bg-emerald/8 text-emerald",
  Community: "bg-emerald/8 text-emerald",
  Beach: "bg-emerald/8 text-emerald",
};

export function localDate(iso, loc, opts) {
  return new Date(iso + "T12:00:00").toLocaleDateString(loc, opts);
}

export function formatRange(startISO, endISO, loc) {
  if (startISO === endISO) return localDate(startISO, loc, { month: "long", day: "numeric", year: "numeric" });
  const s = new Date(startISO + "T12:00:00");
  const e = new Date(endISO + "T12:00:00");
  const startMD = s.toLocaleDateString(loc, { month: "long", day: "numeric" });
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear())
    return `${startMD}\u2013${e.getDate()}, ${e.getFullYear()}`;
  const endFull = e.toLocaleDateString(loc, { month: "long", day: "numeric", year: "numeric" });
  return `${startMD} \u2013 ${endFull}`;
}

function eventJsonLd(ev) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.title,
    startDate: ev.start,
    endDate: ev.end,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: ev.blurb,
    url: ev.url,
    location: {
      "@type": "Place",
      name: ev.venue,
      address: { "@type": "PostalAddress", addressLocality: ev.location, addressRegion: "FL", addressCountry: "US" },
    },
    organizer: { "@id": `${SITE}/#business` },
  };
}

export default function EventsPage() {
  const { lang } = useLanguage();
  const loc = INTL_LOCALE[lang] || "en-US";
  const [data, setData] = useState({ events: [], weekly: [] });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/events`)
      .then((r) => alive && setData(r.data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Filter pills are built from the categories actually present in the calendar.
  const categories = [...new Set(data.events.map((ev) => ev.category))];
  const filtered =
    activeCat === "All" ? data.events : data.events.filter((ev) => ev.category === activeCat);

  // Group filtered events by month
  const groups = [];
  filtered.forEach((ev) => {
    const key = ev.start.slice(0, 7);
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: localDate(ev.start, loc, { month: "long", year: "numeric" }), items: [] };
      groups.push(g);
    }
    g.items.push(ev);
  });

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Events Calendar | Things to Do in Fort Lauderdale & Wilton Manors — Double Rainbow Suites"
        description="An auto-updating calendar of the best things to do in Fort Lauderdale and Wilton Manors over the next 12 months — Stonewall Pride, the Fort Lauderdale International Boat Show, Air Show, Winterfest Boat Parade, Tortuga Music Festival and more. Book your stay around the events you love."
        canonical={`${SITE}/events`}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Events", path: "/events" },
          ]),
          ...data.events.map(eventJsonLd),
        ]}
      />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5 flex items-center gap-2">
            <CalendarBlank size={16} weight="fill" className="text-sunset" /> The local calendar
          </p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-3xl">
            What&apos;s on in Fort Lauderdale & Wilton Manors.
          </h1>
          <p className="text-lg text-emerald-soft max-w-2xl mt-6 font-light">
            A living guide to the year ahead — the signature festivals, parades and beach happenings that
            make South Florida special. Find the dates you love, then book a suite a short walk or ride away.
          </p>
        </motion.div>

        {/* Category filters */}
        {!loading && categories.length > 1 && (
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="event-filters" role="group" aria-label="Filter events by category">
            {["All", ...categories].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                aria-pressed={activeCat === c}
                data-testid={`event-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors duration-300 ${
                  activeCat === c
                    ? "bg-emerald text-sand border-emerald"
                    : "bg-transparent text-emerald border-emerald/25 hover:border-sunset hover:text-sunset"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Timeline grouped by month */}
        <div className="mt-16" data-testid="events-timeline">
          {loading && <p className="text-emerald-soft" data-testid="events-loading">Loading the calendar…</p>}
          {!loading && groups.length === 0 && (
            <p className="text-emerald-soft">No upcoming events found right now — check back soon.</p>
          )}
          {groups.map((g, gi) => (
            <div key={g.key} className="mb-12" data-testid={`events-month-${g.key}`}>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-display text-2xl md:text-3xl text-emerald whitespace-nowrap">{g.label}</h2>
                <span className="h-px flex-1 bg-emerald/12" />
              </div>
              <div className="flex flex-col gap-5">
                {g.items.map((ev, i) => (
                  <motion.article
                    key={ev.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, delay: (i % 3) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`event-card-${ev.id}`}
                    className="group grid md:grid-cols-[180px_1fr] gap-5 md:gap-8 border border-emerald/10 rounded-2xl p-6 md:p-8 bg-sand-2 hover:border-sunset/40 transition-colors duration-300"
                  >
                    <div className="flex md:flex-col md:items-start items-center gap-3 md:gap-2">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${CATEGORY_COLORS[ev.category] || "bg-emerald/8 text-emerald"}`}
                        data-testid={`event-category-${ev.id}`}
                      >
                        {ev.category}
                      </span>
                      <span className="text-emerald font-display text-lg md:text-xl leading-tight" data-testid={`event-date-${ev.id}`}>
                        {formatRange(ev.start, ev.end, loc)}
                      </span>
                      <span
                        data-testid={`event-countdown-${ev.id}`}
                        className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                          countdownTier(ev.start) === "near"
                            ? "bg-sunset text-sand"
                            : countdownTier(ev.start) === "mid"
                            ? "bg-gold/25 text-emerald"
                            : "bg-emerald/8 text-emerald-soft"
                        }`}
                      >
                        {countdownLabel(ev.start)}
                      </span>
                      {!ev.confirmed && (
                        <span className="text-[11px] text-emerald-soft/80">Dates approximate — confirm on the official page</span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl text-emerald leading-snug mb-2">{ev.title}</h3>
                      <p className="flex items-center gap-1.5 text-sm text-emerald-soft mb-3">
                        <MapPin size={14} weight="fill" className="text-sunset" /> {ev.venue} · {ev.location}
                      </p>
                      <p className="text-emerald-soft leading-relaxed font-light">{ev.blurb}</p>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-5">
                        <Link
                          to={`/properties?checkin=${ev.start}&checkout=${ev.checkout}#book`}
                          data-testid={`event-book-${ev.id}`}
                          className="group/btn inline-flex items-center gap-2 rounded-full bg-sunset px-5 py-2.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-emerald"
                        >
                          Book your stay for these dates
                          <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Link>
                        {guideForEvent(ev.id) && (
                          <Link
                            to={`/events/${guideForEvent(ev.id).slug}`}
                            data-testid={`event-guide-link-${ev.id}`}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald underline decoration-sunset/50 decoration-1 underline-offset-4 hover:text-sunset transition-colors duration-300"
                          >
                            Visitor guide
                          </Link>
                        )}
                        <a
                          href={ev.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-link-${ev.id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald hover:text-sunset transition-colors duration-300"
                        >
                          Official event page <ArrowUpRight size={15} weight="bold" />
                        </a>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Weekly staples */}
        {data.weekly.length > 0 && (
          <section className="mt-20" data-testid="events-weekly">
            <div className="flex items-center gap-3 mb-8">
              <Repeat size={22} weight="duotone" className="text-sunset" />
              <div>
                <p className="overline text-sunset">Always on</p>
                <h2 className="font-display text-3xl md:text-4xl text-emerald leading-tight">
                  Neighborhood staples, every week
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
              {data.weekly.map((w, i) => (
                <motion.div
                  key={w.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.06 }}
                  data-testid={`weekly-card-${w.id}`}
                  className="border border-emerald/10 rounded-2xl p-6 md:p-7 bg-sand"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={14} weight="fill" className="text-sunset" />
                    <span className="text-xs font-semibold text-sunset">{w.cadence} · {w.day}</span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-emerald leading-snug mb-2">{w.title}</h3>
                  <p className="flex items-center gap-1.5 text-sm text-emerald-soft mb-3">
                    <MapPin size={14} weight="fill" className="text-sunset" /> {w.venue}
                  </p>
                  <p className="text-emerald-soft leading-relaxed font-light text-sm">{w.blurb}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-20 relative overflow-hidden rounded-3xl bg-emerald text-sand p-10 md:p-16 text-center">
          <p className="overline text-gold mb-4">Plan around the fun</p>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight mb-7 max-w-2xl mx-auto">
            Find your dates, then find your suite.
          </h2>
          <Link
            to="/properties"
            data-testid="events-cta-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-sand hover:text-emerald"
          >
            Explore our stays <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </main>
  );
}
